import 'react-phone-number-input/style.css';
import './phone-input.css';

import { zodResolver } from '@hookform/resolvers/zod';
import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { View } from 'react-native';
import type { CountryCode } from 'react-native-country-picker-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import type { PhoneNumber } from 'react-phone-number-input';
import PhoneInput, {
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input';
import { z } from 'zod';

import { Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { CaretDown } from '@/ui/icons';

import type { PhoneCountry } from './types';

const fieldLabelStyle = {
  fontFamily: 'DMSans-Regular',
  fontSize: 12,
  lineHeight: '16px',
};

const CountrySelectField = ({
  value,
  onChange,
  options,
  iconComponent: FlagIcon,
  arrowComponent: Arrow,
  borderColor,
  backgroundColor,
  label,
  labelColor,
  ...rest
}: any) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginRight: 4,
      }}
    >
      {label && (
        <span style={{ ...fieldLabelStyle, color: labelColor }}>{label}</span>
      )}

      <div
        className="PhoneInputCountry"
        style={{
          boxSizing: 'border-box',
          width: 64,
          height: 48,
          justifyContent: 'center',
          gap: 6,
          padding: '0 12px',
          border: `1px solid ${borderColor}`,
          borderRadius: 16,
          backgroundColor,
        }}
      >
        <select
          {...rest}
          value={value || 'ZZ'}
          onChange={(e) =>
            onChange(e.target.value === 'ZZ' ? undefined : e.target.value)
          }
          className="PhoneInputCountrySelect"
        >
          {options.map(
            ({ value: optionValue, label: optionLabel, divider }: any) => (
              <option
                key={divider ? '|' : optionValue || 'ZZ'}
                value={divider ? '|' : optionValue || 'ZZ'}
                disabled={!!divider}
              >
                {optionLabel}
              </option>
            )
          )}
        </select>

        <FlagIcon country={value} />

        {Arrow ? <Arrow /> : null}
      </div>
    </div>
  );
};

const PhoneNumberField = React.forwardRef<HTMLInputElement, any>(
  ({ label, labelColor, style, ...rest }, ref) => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {label && (
        <span style={{ ...fieldLabelStyle, color: labelColor }}>{label}</span>
      )}

      <input
        ref={ref}
        {...rest}
        style={{ ...style, width: '100%', boxSizing: 'border-box' }}
      />
    </div>
  )
);

const inputPhoneSchema = z
  .object({
    phone: z.string(),
    countryCode: z.string(),
  })
  .refine((data) => isValidPhoneNumber(data.phone), {
    message:
      'Unable to send code to this phone number. Try using a different one or log in with email.',
    path: ['phone'],
  });

export type InputPhoneFormType = z.infer<typeof inputPhoneSchema>;

interface InputPhoneProps {
  onChangeText: (number: string) => void;
  onCountryChange: (country: PhoneCountry) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onSubmitEditing?: () => void;
  defaultCountryCode?: CountryCode;
  phone?: string;
  placeholder?: string;
  style?: any;
  textContainerStyle?: any;
  countryLabel?: string;
  phoneLabel?: string;
}

export const InputPhone = ({
  onChangeText,
  onCountryChange,
  onFocus,
  onBlur,
  onSubmitEditing,
  defaultCountryCode = 'US',
  phone,
  placeholder,
  style,
  textContainerStyle,
  countryLabel,
  phoneLabel,
}: InputPhoneProps) => {
  const { styles, theme } = useStyles(stylesheet);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedCountry, setSelectedCountry] =
    useState<CountryCode>(defaultCountryCode);

  const parsedPhoneNumber = React.useMemo((): PhoneNumber | undefined => {
    if (!phone) return undefined;

    const phoneNumber = parsePhoneNumber(phone, {
      defaultCountry: defaultCountryCode,
    });

    return phoneNumber;
  }, [phone, defaultCountryCode]);

  const {
    control,
    setValue,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm<InputPhoneFormType>({
    defaultValues: {
      phone: parsedPhoneNumber?.number,
      countryCode: defaultCountryCode,
    },
    resolver: zodResolver(inputPhoneSchema),
  });

  React.useEffect(() => {
    if (parsedPhoneNumber) {
      setValue('phone', parsedPhoneNumber.number);
    }
  }, [parsedPhoneNumber]);

  const border = useMemo(() => {
    if (phone) {
      return `1px solid ${theme.colors.input.error.border}`;
    }

    return isFocused
      ? `1px solid ${theme.colors.input.active.border}`
      : `1px solid ${theme.colors.input.default.border}`;
  }, [isFocused, phone]);

  return (
    <View style={[styles.container, style]}>
      <Controller
        name="phone"
        control={control}
        render={({ field: { onChange: onFieldChange, value } }) => {
          return (
            <View style={{ flex: 1, gap: 8 }}>
              <PhoneInput
                style={{
                  width: '100%',
                }}
                containerComponentProps={{
                  style: {
                    flex: 1,
                  },
                }}
                inputComponent={PhoneNumberField}
                numberInputProps={{
                  placeholder,
                  label: phoneLabel,
                  labelColor: theme.colors.text.secondary,
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      onSubmitEditing?.();
                    }
                  },
                  style: {
                    backgroundColor: theme.colors.background_1,
                    borderColor: theme.colors.input.filled.border,
                    borderWidth: 1,
                    color: theme.colors.input.filled.text,
                    placeholderTextColor: theme.colors.input.default.text,
                    paddingLeft: 16,
                    borderRadius: 16,
                    lineHeight: '44px',
                    fontFamily: 'DMSans-Regular',
                    fontSize: 16,
                    border: border,
                    outline: 'none',
                    ...textContainerStyle,
                  },
                }}
                countrySelectComponent={CountrySelectField}
                countrySelectProps={{
                  arrowComponent: () => (
                    <Icon
                      type="naked"
                      icon={<CaretDown />}
                      width={10}
                      height={10}
                      iconStyle={{ paddingLeft: 8, paddingRight: 4 }}
                    />
                  ),
                  borderColor: theme.colors.input.default.border,
                  backgroundColor: theme.colors.background_1,
                  label: countryLabel,
                  labelColor: theme.colors.text.secondary,
                }}
                international
                limitMaxLength
                defaultCountry={selectedCountry}
                onCountryChange={(nextCountry) => {
                  if (nextCountry) {
                    setSelectedCountry(nextCountry as CountryCode);
                  }
                }}
                value={value}
                onBlur={() => {
                  const nationalNumber = value
                    ? parsePhoneNumber(value)?.nationalNumber
                    : undefined;

                  if (nationalNumber) {
                    trigger('phone');
                  }

                  setIsFocused(false);
                  onBlur?.();
                }}
                onFocus={() => {
                  onFocus?.();
                  setIsFocused(true);
                }}
                onChange={(val) => {
                  onFieldChange(val);

                  const parsedNum = val ? parsePhoneNumber(val) : undefined;

                  if (parsedNum?.countryCallingCode) {
                    onCountryChange({
                      callingCode: parsedNum.countryCallingCode,
                    });
                  }

                  setValue('phone', val?.toString() ?? '');
                  onChangeText(parsedNum?.nationalNumber ?? '');

                  if (!parsedNum?.nationalNumber) {
                    clearErrors('phone');
                  } else if (errors.phone) {
                    trigger('phone');
                  }
                }}
                countryCallingCodeEditable={false}
              />

              {errors.phone && (
                <View>
                  <Text style={{ color: theme.colors.input.error.helperText }}>
                    {errors.phone.message}
                  </Text>
                </View>
              )}
            </View>
          );
        }}
      />
    </View>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  container: {
    width: '100%',
    flexDirection: 'row',
    gap: 8,
  },
  countryPickerContainer: {
    width: 108,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: theme.colors.button.secondary.default.border,
    borderWidth: 1,
    borderRadius: 8,
    gap: 8,
  },
}));
